/**
 * RunHostView — spectate-канва ведущего run-вопроса (kind="run", Фаза 14, эпик
 * B3; макет §3.4 спеки phase-14-interface-spec; РЕДИЗАЙН 0.14.1, решение
 * владельца 2026-10-20).
 *
 * Мини-плитка с зонами 25%×25% (та же геометрия, что у игрока: 0.25/0.75),
 * тексты по внешним углам, ПИКСЕЛЬНЫЕ ЧЕЛОВЕЧКИ вместо дотов (цвет per-player
 * из стабильного хэша id — playerColor; ник подписью, >30 игроков — инициалы),
 * дроны-квадрокоптеры из ТОГО ЖЕ seed (B3) с курсовой ориентацией и тенями.
 *
 * Гибель (0.14.1): пятно на месте гибели (positions dead-флаги — координаты
 * замораживаются сервером; плюс reveal dead-лист {playerId,nick,diedAtMs,x,y}
 * — дедуп по id), взрыв у ведущего ТОЖЕ анимируется (по dead-флагу из потока
 * positions): слои взрыва переиспользуются, одновременных взрывов — не более
 * HOST_EXPLOSION_MAX (пул, старейший вытесняется); при >40 игроках взрыв
 * упрощается до вспышки+пятна (без осколков/дыма — DOM не захлебнулся).
 *
 * 0.14.3 — плавность и синхронизация (план phase-14.3-visual-sync §1/§2/§5):
 *  R1 — позиции ВСЕХ спрайтов (фигурки/дроны/тени) — translate3d на обёртке
 *    100%×100% плитки (проценты = доли плитки) — анимация на compositor,
 *    reflow от ~5 Гц-пакетов × N игроков исчезает; CSS transition на left/top
 *    удалён полностью (движение — только rAF-лерп R2);
 *  R2 — буфер-интерполяция: на каждого игрока последние 2 снапшота positions
 *    (координаты + серверное t), рендер на «serverNow − RUN_PLAYBACK_DELAY_MS
 *    (200 мс)»: между снапшотами — лерп, за последним — экстраполяция по
 *    скорости (не далее RUN_EXTRAP_MAX_MS, потом заморозка — в т.ч. после
 *    close, финальный пакет = последний снапшот);
 *  R3 — поворот фигурок: лерп по кратчайшей дуге + гистерезис (<10° — не
 *    вращаем); кадры ходьбы — по EMA-скорости (α=0.2) с гистерезисом
 *    idle↔run (0.02/0.05 плиток/с), частота кадров от накопленного
 *    интерполированного пути (как у RunView);
 *  S1 — симуляция дронов в СЕРВЕРНОМ времени (kindData.startedAtMs/now →
 *    offset; EMA по t каждого positions-пакета, выбросы >500 мс — мимо);
 *  M1/M3 — размеры спрайтов/подписей/счётчиков — доли плитки (--tile из
 *    clientWidth, механизм --ex), спрайт ≈ хитбокс (радиусы из params ×1.5/×1.6),
 *    подписи ников — от плитки с ellipsis; пороги деградации 30/40 сохранены.
 *
 * Счётчики углов считает САМ клиент ведущего по runZone (§3.4; погибшие не
 * считаются — сервер их из зачёта исключает) + счётчик погибших. Reveal:
 * подсветка правильного угла + заморозка (финальный пакет positions уже
 * пришёл при close; rAF-луп погашен — фигурки замирают на последнем transform).
 * Без positions (превью редактора) — статическая плитка без фигурок/дронов.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { isRunDeadReveal, isRunKindData, runCorners, type PlayerPos } from '../../protocol';
import { optionLabel } from '../ui-helpers';
import {
  RUN_PHYSICS,
  RUN_PLAYBACK_DELAY_MS,
  WALK_SPEED_EMA_ALPHA,
  dronePosAt,
  emaStep,
  interpSnapshots,
  isWalkingState,
  makeRunClock,
  playerColor,
  runDifficulty,
  runZone,
  spawnDrones,
  turnToward,
  type PosSnapshot,
  type RunClock,
} from './runEngine';
import { useTilePx } from './runMotion';
import {
  DroneSpriteSvg,
  ExplosionLayers,
  PlayerSpriteSvg,
  SplatSvg,
  SPLAT_FADE_MS,
  SPLAT_TTL_MS,
} from './runSprites';
import type { QuestionBodyProps } from './types';

/** Порог полного ника на фигурке: свыше — инициалы (риск З, §3.4; M3 — сохранён). */
const NICK_FULL_MAX = 30;
/** Порог упрощения взрыва: свыше N игроков — вспышка+пятно без частиц (0.14.1; M3 — сохранён). */
const HOST_EXPLOSION_SIMPLE_PLAYERS = 40;
/** Одновременных взрывов у ведущего: пул слотов, старейший вытесняется. */
const HOST_EXPLOSION_MAX = 6;
/** Плиток пути на кадр ходьбы (частота анимации от скорости; как RunView). */
const HOST_WALK_FRAME_DIST = 0.055;
/** Сглаживание поворота фигурки ведущего (доля дельты за кадр, R3). */
const HOST_TURN_LERP = 0.35;
/** Визуальный коэффициент спрайта игрока к хитбоксу (M2: спрайт ≈ зона поражения). */
const PLAYER_SPRITE_K = 1.5;
/** Визуальный коэффициент спрайта дрона к хитбоксу (M2). */
const DRONE_SPRITE_K = 1.6;

/** Пятно/взрыв у ведущего: зафиксированная гибель игрока. */
interface HostDeath {
  /** playerID (дедуп positions×reveal). */
  id: number;
  /** Ник (для тултипа пятна; reveal-канал богаче positions). */
  nick: string;
  /** Момент гибели (детерминирует форму пятна/осколков). */
  ms: number;
  /** Место гибели (нормализованные координаты). */
  x: number;
  y: number;
}

/** Буфер игрока для R2-интерполяции: последние два снапшота positions. */
interface SnapBuf {
  prev: PosSnapshot | null;
  last: PosSnapshot;
}

/** Анимационное состояние фигурки (R3): курс, кадры, EMA-скорость. */
interface FigureAnim {
  /** Текущий курс (deg; «лицом» вверх = 0). */
  heading: number;
  /** Накопленный интерполированный путь (плиток) → фаза кадров ходьбы. */
  walk: number;
  /** EMA-скорость (плиток/с) — сглаживает 5 Гц-скачки пакетов. */
  speed: number;
  /** Гистерезис idle↔run (состояние кадровой раскладки). */
  running: boolean;
}

export function RunHostView({ question, reveal, positions, positionsT }: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const corners = runCorners(question);
  const kd = question.kindData;
  const seed = kd && isRunKindData(kd) && kd.seed !== undefined ? kd.seed : -1;
  const params = kd && isRunKindData(kd) && kd.params ? kd.params : RUN_PHYSICS;
  const revealed = reveal !== null;
  // useMemo — стабильная ссылка для deps эффектов (lint).
  const players: PlayerPos[] = useMemo(() => positions ?? [], [positions]);

  // Дроны вопроса: один список specs (мемо — рендер и rAF читают одно и то же).
  const drones = useMemo(
    () =>
      spawnDrones(seed, params, runDifficulty(question.index, question.total), question.durationMs),
    [seed, params, question.index, question.total, question.durationMs],
  );

  // ── 0.14.3 S1: серверные часы симуляции (kindData.startedAtMs/now → offset,
  // EMA-уточнение по t positions-пакетов — см. эффект players/positionsT ниже).
  const clock: RunClock = useMemo(
    () => makeRunClock(kd && isRunKindData(kd) ? kd : null, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question.questionId, kd],
  );

  // ── 0.14.3 M1/M2: px-плитка и размеры спрайтов от хитбоксов params.
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const tile = useTilePx(canvasRef);
  const playerSize = 2 * params.playerRadius * tile * PLAYER_SPRITE_K;
  const droneSize = 2 * params.droneRadius * tile * DRONE_SPRITE_K;

  // ── R2: буферы снапшотов per-player (последние 2) + анимация per-player.
  const snapsRef = useRef<Map<number, SnapBuf>>(new Map());
  const animRef = useRef<Map<number, FigureAnim>>(new Map());
  // DOM-обёртки фигурок по id (React рендерит узлы, rAF ведёт transform).
  const figureElsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  // ── Погибшие: кэш по id (positions dead-флаги × reveal dead-лист, дедуп)
  // + активные взрывы (LRU-пул HOST_EXPLOSION_MAX).
  const deathsRef = useRef<Map<number, HostDeath>>(new Map());
  const [deaths, setDeaths] = useState<HostDeath[]>([]);
  const [explosions, setExplosions] = useState<Array<HostDeath & { seq: number }>>([]);
  const seqRef = useRef(0);
  // Пятна — исчезают по таймеру (0.14.2): TTL → fade → удаление из DOM,
  // независимо от фазы (и после close). fadingIds/goneIds — множества по id
  // погибшего; splatTimersRef хранит таймеры пары (fade|gone) на каждое пятно.
  const [fadingIds, setFadingIds] = useState<Set<number>>(new Set());
  const [goneIds, setGoneIds] = useState<Set<number>>(new Set());
  const splatTimersRef = useRef<Map<number, Array<ReturnType<typeof setTimeout>>>>(new Map());

  // Сброс кэша на новый вопрос: пятна, таймеры, фазы гашения, буферы R2 — всё.
  useEffect(() => {
    for (const timers of splatTimersRef.current.values()) {
      for (const t of timers) clearTimeout(t);
    }
    splatTimersRef.current = new Map();
    setFadingIds(new Set());
    setGoneIds(new Set());
    deathsRef.current = new Map();
    setDeaths([]);
    setExplosions([]);
    snapsRef.current = new Map();
    animRef.current = new Map();
    figureElsRef.current = new Map();
  }, [question.questionId]);

  // Жизненный цикл пятна (0.14.2): при появлении в deaths — пара таймеров
  // TTL/fade+remove. Идемпотентно по id (map), чистится эффектом сброса выше.
  useEffect(() => {
    for (const d of deaths) {
      if (splatTimersRef.current.has(d.id)) continue;
      const t1 = setTimeout(
        () => setFadingIds((prev) => new Set(prev).add(d.id)),
        SPLAT_TTL_MS,
      );
      const t2 = setTimeout(
        () => setGoneIds((prev) => new Set(prev).add(d.id)),
        SPLAT_TTL_MS + SPLAT_FADE_MS,
      );
      splatTimersRef.current.set(d.id, [t1, t2]);
    }
  }, [deaths]);

  // Живой поток positions (0.14.3): (1) EMA-уточнение серверного offset по t
  // пакета (S1); (2) новые dead-флаги → пятно + взрыв (0.14.1); (3) буфер
  // снапшотов R2: last → prev, пакет → last (погибших не буферизуем — их
  // фигурок нет, на месте пятна).
  useEffect(() => {
    if (positionsT && positionsT > 0) clock.syncPacketT(positionsT, Date.now());
    if (players.length === 0) return;
    const fresh: HostDeath[] = [];
    for (const p of players) {
      if (p.dead) {
        if (!deathsRef.current.has(p.id)) {
          const d: HostDeath = { id: p.id, nick: p.nick, ms: p.diedAtMs ?? 0, x: p.x, y: p.y };
          deathsRef.current.set(p.id, d);
          fresh.push(d);
        }
        continue;
      }
      const snap: PosSnapshot = { x: p.x, y: p.y, t: positionsT ?? 0 };
      const cur = snapsRef.current.get(p.id);
      if (!cur) {
        snapsRef.current.set(p.id, { prev: null, last: snap });
        if (!animRef.current.has(p.id)) {
          animRef.current.set(p.id, { heading: 0, walk: 0, speed: 0, running: false });
        }
      } else if (snap.t > cur.last.t) {
        snapsRef.current.set(p.id, { prev: cur.last, last: snap });
      } else {
        // Повторный/внеочередной t (коалесинг) — координаты обновляем,
        // prev не трогаем: интерполяция остаётся консистентной.
        snapsRef.current.set(p.id, { prev: cur.prev, last: snap });
      }
    }
    if (fresh.length === 0) return;
    setDeaths(Array.from(deathsRef.current.values()));
    // Взрывы: при >40 игроках — упрощённые (вспышка+пятно, без частиц);
    // одновременных — не более пула (старейший вытесняется, LRU slice).
    setExplosions((prev) => {
      const next = [...prev];
      for (const d of fresh) {
        next.push({ ...d, seq: ++seqRef.current });
      }
      return next.slice(-HOST_EXPLOSION_MAX);
    });
  }, [players, positionsT, clock]);

  // Reveal dead-лист {playerId,nick,diedAtMs,x,y}: пятна для всех погибших
  // (даже отвалившихся до финального пакета); БЕЗ взрывов — вопрос закрыт.
  const revealDead = reveal?.reveal !== undefined && isRunDeadReveal(reveal.reveal) ? reveal.reveal.dead : null;
  useEffect(() => {
    if (!revealDead) return;
    let added = false;
    for (const d of revealDead) {
      if (deathsRef.current.has(d.playerId)) continue;
      deathsRef.current.set(d.playerId, {
        id: d.playerId,
        nick: d.nick,
        ms: d.diedAtMs,
        x: d.x,
        y: d.y,
      });
      added = true;
    }
    if (added) setDeaths(Array.from(deathsRef.current.values()));
  }, [revealDead]);

  // ── ЕДИНЫЙ rAF-луп (0.14.3): дроны (S1-серверное время) + фигурки игроков
  // (R2-интерполяция на serverNow−200 мс, R3-поворот/кадры). Всё — translate3d
  // на обёртках 100%×100% (R1, compositor). Гасится при reveal — финальная
  // сцена заморожена на последнем transform.
  const droneElsRef = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    droneElsRef.current = [];
    if (revealed || drones.length === 0) return;
    let raf = 0;
    let lastFrame = performance.now();
    const frame = (): void => {
      const now = performance.now();
      const frameDt = Math.min(100, now - lastFrame);
      lastFrame = now;
      const localNow = Date.now();
      // S1: фаза дронов от серверного startedAtMs (EMA-offset внутри clock).
      const elapsed = clock.elapsedMs(localNow, question.startedAt);
      for (let i = 0; i < drones.length; i++) {
        const el = droneElsRef.current[i];
        if (!el) continue;
        const spec = drones[i];
        const at = dronePosAt(spec, elapsed);
        if (at === null) {
          el.style.display = 'none';
          continue;
        }
        // R1: translate3d на обёртке плитки (проценты = доли плитки);
        // поворот — на вложенном .run-drone (тень — статична внутри обёртки).
        el.style.display = '';
        el.style.transform = `translate3d(${(at.x * 100).toFixed(3)}%, ${(at.y * 100).toFixed(3)}%, 0)`;
        const body = el.firstElementChild as HTMLElement | null;
        if (body) {
          body.style.transform = `translate(-50%, -50%) rotate(${(Math.atan2(spec.dy, spec.dx) * 180) / Math.PI + 90}deg)`;
        }
      }
      // R2: фигурки — рендер на serverNow − playback delay, лерп между
      // снапшотами; R3 — курс (кратчайшая дуга + гистерезис) и кадры (EMA).
      const renderT = clock.serverNow(localNow) - RUN_PLAYBACK_DELAY_MS;
      for (const [id, buf] of snapsRef.current) {
        const el = figureElsRef.current.get(id);
        if (!el) continue;
        const ip = interpSnapshots(buf.prev, buf.last, renderT);
        el.style.transform = `translate3d(${(ip.x * 100).toFixed(3)}%, ${(ip.y * 100).toFixed(3)}%, 0)`;
        let a = animRef.current.get(id);
        if (!a) {
          a = { heading: 0, walk: 0, speed: 0, running: false };
          animRef.current.set(id, a);
        }
        a.speed = emaStep(a.speed, Math.hypot(ip.vx, ip.vy) * 1000, WALK_SPEED_EMA_ALPHA);
        a.running = isWalkingState(a.running, a.speed);
        if (a.running) {
          a.walk += a.speed * (frameDt / 1000);
          const target = (Math.atan2(ip.vy, ip.vx) * 180) / Math.PI + 90;
          a.heading = turnToward(a.heading, target, HOST_TURN_LERP);
        }
        el.dataset.frame = a.running
          ? String(Math.floor(a.walk / HOST_WALK_FRAME_DIST) % 4)
          : '1';
        const body = el.firstElementChild as HTMLElement | null;
        if (body) {
          body.style.transform = `translate(-50%, -50%) rotate(${a.heading.toFixed(1)}deg)`;
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [question.questionId, question.startedAt, drones, revealed, clock]);

  // Монтирование узла фигурки: transform сразу — из буфера или последнего
  // пакета (эффект буфера ещё не отработал), иначе один кадр фигурка висит
  // в углу (0,0) до первого rAF-тика.
  function bindFigure(id: number): (el: HTMLDivElement | null) => void {
    return (el) => {
      if (el === null) {
        figureElsRef.current.delete(id);
        return;
      }
      figureElsRef.current.set(id, el);
      if (el.style.transform) return;
      const buf = snapsRef.current.get(id);
      const p = buf ? buf.last : players.find((pl) => pl.id === id);
      if (p) {
        el.style.transform = `translate3d(${(p.x * 100).toFixed(3)}%, ${(p.y * 100).toFixed(3)}%, 0)`;
      }
    };
  }

  // ── Счётчики углов: клиентский подсчёт по геометрии сервера (§3.4);
  // погибшие выбыли — в зонах не считаются (сервер их из зачёта исключает).
  const counts = [0, 0, 0, 0];
  let outside = 0;
  let alive = 0;
  for (const p of players) {
    if (p.dead) continue;
    alive += 1;
    const z = runZone(p.x, p.y);
    if (z >= 0) counts[z] += 1;
    else outside += 1;
  }
  const deadTotal = deaths.length;
  // 0.14.2 (п.8, замечание владельца): выжившие из positions-потока; без
  // потока (превью редактора) — прочерк: неизвестное ≠ нулю.
  const aliveTotal: number | null = players.length > 0 ? alive : null;
  const shortNick = players.length > NICK_FULL_MAX;
  const simpleExplosions = players.length > HOST_EXPLOSION_SIMPLE_PLAYERS;

  return (
    <div
      className={revealed ? 'run-host run-host--frozen' : 'run-host'}
      style={
        {
          // 0.14.3 M1/M2/M3: плитка в px (механизм --ex, обобщён на канву) +
          // размеры спрайтов от хитбоксов params (спрайт ≈ зона поражения);
          // подписи/счётчики — доли плитки через --tile в CSS.
          '--tile': `${tile}px`,
          '--player-size': `${playerSize.toFixed(1)}px`,
          '--drone-size': `${droneSize.toFixed(1)}px`,
        } as CSSProperties
      }
    >
      <div
        className={revealed ? 'run-tile run-tile--frozen' : 'run-tile'}
        ref={canvasRef}
        data-testid="host-run-canvas"
        role="img"
        aria-label={t('run.cornerHint')}
      >
        {/* Зоны зачёта 25%×25% — как у игрока (0.25/0.75, редизайн 0.14.1). */}
        {corners.map((_, i) => (
          <div
            key={`z-${i}`}
            className={
              'run-zone run-zone--' + i + (revealed && reveal?.correctOption === i ? ' run-zone--correct' : '')
            }
            data-testid={`host-run-zone-${i}`}
            aria-hidden="true"
          />
        ))}

        {/* Тексты по внешним углам плитки (0.14.1). */}
        {corners.map((corner, i) => (
          <div key={`c-${i}`} className={'run-corner run-corner--' + i} data-testid={`host-run-corner-${i}`}>
            <span className="run-corner__label">
              <span className="run-corner__index">{optionLabel(i)}</span>
              <span className="run-corner__text">{corner}</span>
            </span>
          </div>
        ))}

        {/* Нейтральные границы 0.25/0.75 — как у игрока (§3.2). */}
        <div className="run-grid-line run-grid-line--v" style={{ left: '25%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--v" style={{ left: '75%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--h" style={{ top: '25%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--h" style={{ top: '75%' }} aria-hidden="true" />

        {/* Пятна погибших (0.14.1); 0.14.2 — исчезают по таймеру (TTL → fade
            → удаление из DOM) в любом состоянии вопроса, включая reveal. */}
        {deaths
          .filter((d) => !goneIds.has(d.id))
          .map((d) => (
            <div
              key={`splat-${d.id}`}
              className={'run-splat run-splat--instant' + (fadingIds.has(d.id) ? ' run-splat--fade' : '')}
              data-testid={`host-run-splat-${d.id}`}
              style={{ left: `${d.x * 100}%`, top: `${d.y * 100}%` }}
              aria-hidden="true"
            >
              <SplatSvg diedAtMs={d.ms} />
            </div>
          ))}

        {/* Взрывы (LRU-пул): при >40 игроках — вспышка+пятно без частиц. */}
        {explosions.map((e) => (
          <div
            key={`exp-${e.seq}`}
            className={'run-explosion' + (simpleExplosions ? ' run-explosion--simple' : '')}
            data-testid={`host-run-explosion-${e.id}`}
            style={
              {
                left: `${e.x * 100}%`,
                top: `${e.y * 100}%`,
                '--ex': `${tile}px`,
              } as CSSProperties
            }
            aria-hidden="true"
          >
            <ExplosionLayers diedAtMs={e.ms} simple={simpleExplosions} />
          </div>
        ))}

        {/* Дроны + тени (R1): обёртка 100%×100% плитки с translate3d из rAF;
            внутри — тень (статичное смещение «под» дроном) и повернутый по
            курсу спрайт. display управляется на обёртке целиком. */}
        {drones.map((_, i) => (
          <div
            key={`drone-${i}`}
            className="run-drone-shell"
            ref={(el) => {
              droneElsRef.current[i] = el;
            }}
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            <span className="run-shadow" />
            <div className="run-drone">
              <DroneSpriteSvg />
              <span className="run-drone__trail" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </div>
          </div>
        ))}

        {/* Пиксельные человечки per-player (цвет — стабильный хэш id); погибшие
            не рендерятся (на их месте пятно). R1: позиция — translate3d на
            обёртке плитки (rAF, интерполяция R2); R3: rotate + data-frame на
            обёртке, кадры/курс — от EMA-скорости интерполированного пути. */}
        {players.map((p) =>
          p.dead ? null : (
            <div
              key={p.id}
              className="run-figure"
              data-testid={`host-run-player-${p.id}`}
              data-frame="1"
              ref={bindFigure(p.id)}
            >
              <div className="run-figure__body">
                <PlayerSpriteSvg color={playerColor(p.id)} />
                <span className="run-figure__label">{shortNick ? p.nick.slice(0, 2) : p.nick}</span>
              </div>
            </div>
          ),
        )}
      </div>

      <div className="run-host__counts">
        {counts.map((n, i) => (
          <span className="run-host__count" key={i} data-testid={`host-run-corner-count-${i}`}>
            {t('run.zoneIndicator', { n: i + 1, corner: corners[i] ?? '' })}: {n}
          </span>
        ))}
        <span className="run-host__count" data-testid="host-run-outside-count">
          {t('run.host.outside', { n: outside })}
        </span>
        {/* 0.14.2 (п.8): выжившие рядом с погибшими; null (нет потока) — прочерк. */}
        <span className="run-host__count" data-testid="host-run-alive-count">
          {t('run.host.alive', { n: aliveTotal ?? '—' })}
        </span>
        {deadTotal > 0 ? (
          <span className="run-host__count run-host__count--dead" data-testid="host-run-dead-count">
            {t('run.host.dead', { n: deadTotal })}
          </span>
        ) : null}
      </div>
      {revealed && reveal?.correctOption !== undefined ? (
        <p className="q-hint q-hint--ok" data-testid="host-run-reveal">
          {t('run.reveal.correct', { corner: corners[reveal.correctOption] ?? '' })}
        </p>
      ) : null}
    </div>
  );
}
